import * as styles from "./DateRecurrenceEditor.css";

import { clsx } from "clsx";
import { type JSX, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { NumberField } from "@frontend/components/atoms/NumberField";
import { Radio, RadioGroup } from "@frontend/components/atoms/Radio";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsPanel,
  TabsTab,
} from "@frontend/components/atoms/Tabs";
import { useLingui } from "@lingui/react/macro";

import type {
  RecurrenceDayTarget,
  RecurrenceMonthMode,
  RecurrencePosition,
  RecurrenceTab,
  RecurrenceValue,
  RecurrenceWeekday,
} from "./DateRecurrencePicker.types";
import { DateRecurrenceSchedule } from "./DateRecurrenceSchedule";
import {
  RECURRENCE_MONTHS,
  RECURRENCE_MONTH_DAYS,
  RECURRENCE_TABS,
  RECURRENCE_WEEKDAYS,
  RECURRENCE_WORKING_DAYS,
} from "./constants";
import { parseRecurrence, serializeRecurrence } from "./recurrenceRule";

interface DateRecurrenceEditorProps {
  value?: string | null;
  onValueChange?: (value: string) => void;
  startDate?: string | null;
  onStartDateChange?: (value: string | null) => void;
  showEnd?: boolean;
  locale?: string;
}

export function DateRecurrenceEditor({
  value,
  onValueChange,
  startDate,
  onStartDateChange,
  showEnd = false,
  locale,
}: DateRecurrenceEditorProps): JSX.Element {
  const { t } = useLingui();

  const rule = parseRecurrence(value);

  // The tab is not always readable from the rule: a Monday to Friday weekly
  // rule is both "every weekday" and the Daily tab's business day. Keeping it
  // in state stops the panel from jumping while the rule is being edited.
  const [tab, setTab] = useState<RecurrenceTab>(() => tabOf(rule));

  const emitted = useRef<string | null>(null);

  useEffect(() => {
    if (emitted.current === (value ?? "")) {
      return;
    }

    emitted.current = null;
    setTab(tabOf(parseRecurrence(value)));
  }, [value]);

  const current: RecurrenceValue = {
    ...rule,
    frequency: tab,
    businessDays: "daily" === tab && rule.businessDays,
  };

  function emit(next: RecurrenceValue) {
    const text = serializeRecurrence(next);

    emitted.current = text;
    onValueChange?.(text);
  }

  function update(patch: Partial<RecurrenceValue>) {
    emit({ ...current, ...patch });
  }

  function selectTab(next: RecurrenceTab) {
    setTab(next);
    emit({ ...current, frequency: next });
  }

  function toggleIn<T>(list: T[], entry: T): T[] {
    return list.includes(entry)
      ? list.filter((item) => item !== entry)
      : [...list, entry];
  }

  const positions: { value: string; label: string }[] = [
    { value: "1", label: t`first` },
    { value: "2", label: t`second` },
    { value: "3", label: t`third` },
    { value: "4", label: t`fourth` },
    { value: "5", label: t`fifth` },
    { value: "-1", label: t`last` },
  ];

  const targets: { value: RecurrenceDayTarget; label: string }[] = [
    ...RECURRENCE_WEEKDAYS.map((day) => ({
      value: day as RecurrenceDayTarget,
      label: weekdayName(day, locale, "long"),
    })),
    { value: "day", label: t`day` },
    { value: "weekday", label: t`weekday` },
    { value: "weekendday", label: t`weekend day` },
  ];

  const tabLabels: Record<RecurrenceTab, string> = {
    daily: t`Daily`,
    weekly: t`Weekly`,
    monthly: t`Monthly`,
    yearly: t`Yearly`,
  };

  const units: Record<RecurrenceTab, string> =
    current.interval > 1
      ? {
          daily: t`days`,
          weekly: t`weeks on:`,
          monthly: t`months`,
          yearly: t`years`,
        }
      : {
          daily: t`day`,
          weekly: t`week on:`,
          monthly: t`month`,
          yearly: t`year`,
        };

  const interval = (
    <NumberField
      layout="text"
      min={1}
      max={999}
      value={current.interval}
      disabled={current.businessDays}
      onValueChange={(next) => update({ interval: Math.max(1, next ?? 1) })}
    />
  );

  function every(entry: RecurrenceTab) {
    return (
      <>
        <span className={styles.label}>{t`Every`}</span>
        {interval}
        <span className={styles.mutedLabel}>{units[entry]}</span>
      </>
    );
  }

  const positionRow = (
    <div className={styles.row}>
      <span className={styles.mutedLabel}>{t`on the`}</span>

      <Select
        items={positions}
        value={String(current.position)}
        onValueChange={(next) =>
          update({ position: Number(next) as RecurrencePosition })
        }
      >
        <SelectTrigger className={styles.positionSelect}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {positions.map((position) => (
            <SelectItem key={position.value} value={position.value}>
              {position.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        items={targets}
        value={current.positionTarget}
        onValueChange={(next) =>
          update({ positionTarget: next as RecurrenceDayTarget })
        }
      >
        <SelectTrigger className={styles.positionSelect}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {targets.map((target) => (
            <SelectItem key={target.value} value={target.value}>
              {target.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const modeRadios = (
    <RadioGroup
      orientation="horizontal"
      layout="text"
      value={current.monthMode}
      onValueChange={(next) =>
        update({ monthMode: next as RecurrenceMonthMode })
      }
    >
      <Radio value="position" label={t`on a position`} />
      <Radio value="days" label={t`on these days`} />
    </RadioGroup>
  );

  const monthDayGrid = (
    <div className={styles.monthDayGrid}>
      {RECURRENCE_MONTH_DAYS.map((day) => (
        <button
          key={day}
          type="button"
          className={styles.monthDay}
          aria-pressed={current.monthDays.includes(day)}
          onClick={() =>
            update({ monthDays: toggleIn(current.monthDays, day) })
          }
        >
          {String(day).padStart(2, "0")}
        </button>
      ))}
    </div>
  );

  return (
    <div data-slot="date-recurrence-editor" className={styles.editor}>
      <DateRecurrenceSchedule
        end={current}
        onEndChange={(next) => update(next)}
        startDate={startDate}
        onStartDateChange={onStartDateChange}
        showEnd={showEnd}
        locale={locale}
      />

      <Tabs
        size="sm"
        variant="segmented"
        className={styles.frequency}
        value={tab}
        onValueChange={(next) => selectTab(next as RecurrenceTab)}
      >
        <TabsList>
          {RECURRENCE_TABS.map((entry) => (
            <TabsTab key={entry} value={entry} className={styles.tab}>
              {tabLabels[entry]}
            </TabsTab>
          ))}

          <TabsIndicator />
        </TabsList>

        <TabsContent className={styles.section}>
          <TabsPanel
            value="daily"
            keepMounted={false}
            className={styles.frequencyPanel}
          >
            <RadioGroup
              orientation="horizontal"
              layout="text"
              value={current.businessDays ? "business" : "interval"}
              onValueChange={(next) =>
                update({ businessDays: "business" === next })
              }
            >
              <Radio value="interval" label={t`Every`} />
              {interval}
              <span className={styles.mutedLabel}>{units.daily}</span>
              <Radio value="business" label={t`Business day`} />
            </RadioGroup>
          </TabsPanel>

          <TabsPanel
            value="weekly"
            keepMounted={false}
            className={styles.frequencyPanel}
          >
            <div className={styles.sentence}>{every("weekly")}</div>

            <div className={styles.dayGrid}>
              {RECURRENCE_WEEKDAYS.map((day) => (
                <Tile
                  key={day}
                  label={weekdayName(day, locale, "short")}
                  title={weekdayName(day, locale, "long")}
                  pressed={current.weekdays.includes(day)}
                  onClick={() =>
                    update({
                      weekdays: sortWeekdays(toggleIn(current.weekdays, day)),
                    })
                  }
                />
              ))}
            </div>

            <div className={styles.quickActions}>
              <Button
                variant="ghost"
                size="sm"
                label={t`Mon-Fri`}
                onClick={() =>
                  update({ weekdays: [...RECURRENCE_WORKING_DAYS] })
                }
              />
              <Button
                variant="ghost"
                size="sm"
                label={t`All`}
                onClick={() => update({ weekdays: [...RECURRENCE_WEEKDAYS] })}
              />
              <Button
                variant="ghost"
                size="sm"
                label={t`Clear`}
                onClick={() => update({ weekdays: [] })}
              />
            </div>
          </TabsPanel>

          <TabsPanel
            value="monthly"
            keepMounted={false}
            className={styles.frequencyPanel}
          >
            <div className={styles.sentence}>
              {every("monthly")}
              {modeRadios}
            </div>

            {"days" === current.monthMode ? monthDayGrid : positionRow}
          </TabsPanel>

          <TabsPanel
            value="yearly"
            keepMounted={false}
            className={styles.frequencyPanel}
          >
            <div className={styles.sentence}>
              {every("yearly")}
              {modeRadios}
            </div>

            <MonthStrip
              months={current.months}
              locale={locale}
              onToggle={(month) =>
                update({ months: toggleIn(current.months, month) })
              }
            />

            {"days" === current.monthMode ? monthDayGrid : positionRow}
          </TabsPanel>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * The twelve months on one line. Six of them fit at most, so the arrows scroll
 * the strip rather than the panel growing a second row.
 */
function MonthStrip({
  months,
  locale,
  onToggle,
}: {
  months: number[];
  locale?: string;
  onToggle: (month: number) => void;
}): JSX.Element {
  const { t } = useLingui();

  const scroller = useRef<HTMLDivElement>(null);

  const [edges, setEdges] = useState({ start: true, end: false });

  function readEdges() {
    const element = scroller.current;

    if (!element) {
      return;
    }

    setEdges({
      start: element.scrollLeft <= 1,
      end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 1,
    });
  }

  // Opening on a rule that keeps July should not hide it off to the right.
  useLayoutEffect(() => {
    const element = scroller.current;

    if (!element) {
      return;
    }

    const first =
      months.length > 0 ? element.children[Math.min(...months) - 1] : null;

    if (first instanceof HTMLElement) {
      const offset =
        first.getBoundingClientRect().left -
        element.getBoundingClientRect().left;

      // Not scrollIntoView, which would scroll the page along with the strip,
      // and instant so the panel opens in place rather than scrolling itself.
      element.scrollTo({
        left: element.scrollLeft + offset,
        behavior: "instant",
      });
    }

    readEdges();
    // Only on open: afterwards the strip is the user's to scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function scroll(direction: 1 | -1) {
    scroller.current?.scrollBy({
      left: direction * scroller.current.clientWidth * 0.8,
    });
  }

  return (
    <div className={styles.strip}>
      <div ref={scroller} className={styles.stripScroller} onScroll={readEdges}>
        {RECURRENCE_MONTHS.map((month) => (
          <Tile
            key={month}
            className={styles.stripTile}
            label={monthName(month, locale, "short")}
            title={monthName(month, locale, "long")}
            pressed={months.includes(month)}
            onClick={() => onToggle(month)}
          />
        ))}
      </div>

      {/* Shown only while there is something that way, so a tile at either end
          never hides behind an arrow that can do nothing. */}
      {!edges.start && (
        <span className={styles.stripArrowStart}>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t`Earlier months`}
            onClick={() => scroll(-1)}
            leftSection={<Icon name="ChevronLeft" size="sm" />}
          />
        </span>
      )}

      {!edges.end && (
        <span className={styles.stripArrowEnd}>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t`Later months`}
            onClick={() => scroll(1)}
            leftSection={<Icon name="ChevronRight" size="sm" />}
          />
        </span>
      )}
    </div>
  );
}

/** A weekday or a month, drawn as the page of a calendar. */
function Tile({
  label,
  title,
  pressed,
  onClick,
  className,
}: {
  label: string;
  title: string;
  pressed: boolean;
  onClick: () => void;
  className?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={clsx(styles.tile, className)}
      title={title}
      aria-pressed={pressed}
      onClick={onClick}
    >
      <span className={styles.tileBand}>
        <span className={styles.tileRing} />
        <span className={styles.tileRing} />
      </span>
      <span className={styles.tileLabel}>{label}</span>
    </button>
  );
}

function tabOf(value: RecurrenceValue): RecurrenceTab {
  return "none" === value.frequency ? "weekly" : value.frequency;
}

function sortWeekdays(days: RecurrenceWeekday[]): RecurrenceWeekday[] {
  return [...days].sort(
    (a, b) => RECURRENCE_WEEKDAYS.indexOf(a) - RECURRENCE_WEEKDAYS.indexOf(b),
  );
}

/**
 * Monday first, whatever the locale would put there.
 */
function weekdayName(
  day: RecurrenceWeekday,
  locale: string | undefined,
  weekday: "short" | "long",
): string {
  const date = new Date(
    Date.UTC(2024, 0, 1 + RECURRENCE_WEEKDAYS.indexOf(day)),
  );

  return new Intl.DateTimeFormat(locale, { weekday, timeZone: "UTC" }).format(
    date,
  );
}

function monthName(
  month: number,
  locale: string | undefined,
  format: "short" | "long",
): string {
  const date = new Date(Date.UTC(2024, month - 1, 1));

  return new Intl.DateTimeFormat(locale, {
    month: format,
    timeZone: "UTC",
  }).format(date);
}
